<!--
  File name: ActivityLogsInput.vue
  Program Description:
    - This component renders the specific field component according
    to the 'fieldName' prop.
    - It is used by the activity logs to determine the right component to display.
  Includes:
    - ActivityLogsInput
-->
<template>
  <div>
    <template v-if="fieldName === 'natureOfPersonalDataInvolved'">
      <v-checkbox
        v-model="toJson(value).checklist"
        readonly
        hide-details
        label="Sensitive Personal Information Involved"
        value="Sensitive Personal Information Involved"
      />
      <v-checkbox
        v-model="toJson(value).checklist"
        readonly
        hide-details
        label="Information that could enable identity fraud"
        value="Information that could enable identity fraud"
      />
      <!-- sub checkbox -->
      <v-checkbox
        v-model="toJson(value).additionalChecklist"
        class="ml-6"
        readonly
        hide-details
        label="Data about the financial or economic situation of the data subject"
        value="Data about the financial or economic situation of the data subject"
      />
      <v-checkbox
        v-model="toJson(value).additionalChecklist"
        class="ml-6"
        readonly
        hide-details
        label="Usernames, passwords and other login data"
        value="Usernames, passwords and other login data"
      />
      <v-checkbox
        v-model="toJson(value).additionalChecklist"
        class="ml-6"
        readonly
        hide-details
        label="Biometric data"
        value="Biometric data"
      />
      <v-checkbox
        v-model="toJson(value).additionalChecklist"
        class="ml-6"
        readonly
        hide-details
        label="Copies of identification documents"
        value="Copies of identification documents"
      />
      <v-checkbox
        v-model="toJson(value).additionalChecklist"
        class="ml-6"
        readonly
        hide-details
        label="Licenses or unique identifiers like Philhealth, SSS, GSIS, TIN number"
        value="Licenses or unique identifiers like Philhealth, SSS, GSIS, TIN number"
      />
      <v-checkbox
        v-model="toJson(value).additionalChecklist"
        class="ml-6"
        readonly
        hide-details
        label="Information which may be made the basis of grant of rights or benefits"
        value="Information which may be made the basis of grant of rights or benefits"
      />
      <!-- end of sub checkbox -->
      <v-checkbox
        v-model="toJson(value).checklist"
        readonly
        hide-details
        label="Information that would likely affect national security, public safety, public order, or public health"
        value="Information that would likely affect national security, public safety, public order, or public health"
      />
      <v-checkbox
        v-model="toJson(value).checklist"
        readonly
        hide-details
        label="Information required by applicable laws or rules be confidential"
        value="Information required by applicable laws or rules be confidential"
      />
      <v-checkbox
        v-model="toJson(value).checklist"
        readonly
        hide-details
        label="Others"
        value="Others"
      />
      <v-textarea
        :value="toJson(value).specify"
        class="cursor my-7"
        readonly
        outlined
        disabled
        hide-details
      >
        <template #label>
          <h3>Specify Others</h3>
        </template>
      </v-textarea>
      <v-textarea
        :value="toJson(value).remarks"
        class="cursor mb-4"
        readonly
        outlined
        disabled
        hide-details
      >
        <template #label>
          <h3>Please specify personal information and/or sensitive personal information involved</h3>
        </template>
      </v-textarea>
    </template>
    <template v-else-if="fieldName === 'measuresInPlace'">
      <v-checkbox
        v-model="toJson(value).checklist"
        readonly
        hide-details
        label="Security measures would prevent unauthorized use or access of the personal data."
        value="Security measures would prevent unauthorized use or access of the personal data."
      />
      <v-checkbox
        v-model="toJson(value).checklist"
        readonly
        hide-details
        label="Subsequent measures taken would ensure that the risk of harm or negative consequence to the data subjects will not materialize."
        value="Subsequent measures taken would ensure that the risk of harm or negative consequence to the data subjects will not materialize."
      />
      <v-textarea
        v-model="toJson(value).remarks"
        class="cursor my-4"
        outlined
        disabled
        readonly
        hide-details
      >
        <template #label>
          <h3>Remarks</h3>
        </template>
      </v-textarea>
    </template>
    <template v-else-if="fieldName === 'noInformation'">
      <template v-for="item in noInfoItems">
        <div :key="item.label">
          <h3>{{ item.label }}</h3>
          <v-textarea
            :value="item.value"
            class="cursor mb-5"
            hide-details
            disabled
            readonly
          >
            <template #label>
              <h3>Provide Details</h3>
            </template>
          </v-textarea>
        </div>
      </template>
    </template>
    <template v-else-if="fieldName === 'timelineOfActionsTaken'">
      <h3 class="mb-2">
        {{ label }}
      </h3>
      <div
        v-for="item,i in items"
        :key="i"
      >
        <v-divider
          v-if="i !== 0"
          class="my-4"
        />
        <v-text-field
          :value="value && item.actionTaken"
          outlined
          disabled
        >
          <template #label>
            <h3>Action Taken</h3>
          </template>
        </v-text-field>
        <v-text-field
          :value="value && toDateString(item.date)"
          outlined
          disabled
        >
          <template #label>
            <h3>Date</h3>
          </template>
        </v-text-field>
        <v-text-field
          :value="value && item.timeliness"
          outlined
          disabled
          hide-details
        >
          <template #label>
            <h3>Timeliness of Compliance</h3>
          </template>
        </v-text-field>
      </div>
    </template>
    <template v-else-if="fieldName === 'documentsRequired'">
      <template v-for="item, i in items">
        <div
          :key="i"
          class="mb-6"
        >
          <v-text-field
            dense
            disabled
            outlined
            :value="value && item.document"
          >
            <template #label>
              <h3>Document</h3>
            </template>
          </v-text-field>
          <v-autocomplete
            :value="value && item.legalBasisForRequirement"
            :items="item.legalBasisForRequirement"
            label="Legal Basis for Requirement"
            disabled
            outlined
            multiple
            chips
          />
          <v-text-field
            dense
            disabled
            outlined
            :value="value && item.statusOfCompliance"
          >
            <template #label>
              <h3>Status of compliance</h3>
            </template>
          </v-text-field>
          <v-text-field
            dense
            disabled
            outlined
            :value="value && toDateString(item.dateOfCompliance)"
          >
            <template #label>
              <h3>Date of compliance</h3>
            </template>
          </v-text-field>
        </div>
      </template>
    </template>
    <template v-else-if="fieldName === 'notificationType'">
      <div
        v-for="item, i in toJson(value === '[]' ? newValue : value)"
        :key="i"
      >
        <v-row>
          <v-col>
            <v-checkbox
              v-model="item.value"
              :value="value === '[]' ? ' ' : item.value"
              :label="item.value"
              hide-details
              readonly
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pt-0">
            <v-text-field
              v-if="item.subtype"
              :value="value === '[]' ? null : item.subtype"
              hide-details
              disabled
              outlined
              dense
            >
              <template #label>
                <h3>Select one</h3>
              </template>
            </v-text-field>
          </v-col>
          <v-col
            class="pt-0"
            :cols="item.subtype ? '6' : '12'"
          >
            <v-textarea
              :value="value === '[]' ? null : item.remarks"
              class="cursor"
              rows="1"
              hide-details
              disabled
              readonly
              outlined
              dense
            >
              <template #label>
                <h3>Remarks</h3>
              </template>
            </v-textarea>
          </v-col>
        </v-row>
      </div>
    </template>
    <template v-else-if="fieldName === 'statusOfCompliance'">
      <template v-for="item,i in statusOfComplianceItems">
        <div
          :key="i"
          class="mb-6"
        >
          <v-text-field
            dense
            disabled
            outlined
            :value="item.type"
          >
            <template #label>
              <h3>Documents Required</h3>
            </template>
          </v-text-field>
          <v-text-field
            dense
            disabled
            outlined
            value="Section 17, NPC Circular 2016-03"
          >
            <template #label>
              <h3>Legal Basis for Requirement</h3>
            </template>
          </v-text-field>
          <v-text-field
            dense
            disabled
            outlined
            :value="item.statusOfCompliance"
          >
            <template #label>
              <h3>Status of Compliance</h3>
            </template>
          </v-text-field>
          <v-text-field
            dense
            disabled
            outlined
            :value="toDateString(item.dateOfCompliance)"
          >
            <template #label>
              <h3>Date of Compliance</h3>
            </template>
          </v-text-field>
        </div>
      </template>
    </template>
    <template v-else-if="fieldName === 'recommendation'">
      <v-text-field
        disabled
        outlined
        :value="value && items.overall"
      >
        <template #label>
          <h3>Over all level of compliance</h3>
        </template>
      </v-text-field>
      <v-text-field
        disabled
        outlined
        :value="value && items.recommendation"
      >
        <template #label>
          <h3>Recommendation</h3>
        </template>
      </v-text-field>
      <v-textarea
        class="cursor"
        disabled
        outlined
        readonly
        :value="value && items.remarks"
      >
        <template #label>
          <h3>Remarks</h3>
        </template>
      </v-textarea>
    </template>
    <template v-else-if="fieldName === 'fbner.representative'">
      <v-text-field
        :value="toJson(value).name"
        disabled
        outlined
      >
        <template #label>
          <h3>Name of Representative, if no appointed DPO</h3>
        </template>
      </v-text-field>
      <v-text-field
        :value="toJson(value).officeAddress"
        disabled
        outlined
      >
        <template #label>
          <h3>Office Address</h3>
        </template>
      </v-text-field>
      <v-text-field
        :value="toJson(value).emailAddress"
        disabled
        outlined
      >
        <template #label>
          <h3>Email Address</h3>
        </template>
      </v-text-field>
      <v-text-field
        :value="toJson(value).mobileOrOfficeNumber"
        disabled
        outlined
      >
        <template #label>
          <h3>Mobile/Office Number</h3>
        </template>
      </v-text-field>
    </template>
    <template v-else-if="component === 'checkbox'">
      <v-checkbox
        v-model="inputValue(component, value).model"
        :label="label"
        readonly
      />
    </template>
    <template v-else-if="component === 'v-checkbox'">
      <h3>{{ label }}</h3>
      <v-checkbox
        v-for="item,i in checkboxItems(inputValue(component, value).model)"
        :key="i"
        v-model="inputValue(component, value).model"
        :label="item.label"
        :value="item.value"
        readonly
      />
    </template>
    <template v-else-if="component === 'radio'">
      <h3>{{ label }}</h3>
      <v-radio-group
        v-model="inputValue(component, value).model"
        row
      >
        <v-radio
          :label="radioButtonValue[fieldName].left"
          :value="radioButtonValue[fieldName].left"
          readonly
        />
        <v-radio
          :label="radioButtonValue[fieldName].right"
          :value="radioButtonValue[fieldName].right"
          readonly
        />
      </v-radio-group>
    </template>
    <template v-else-if="component === 'textareas'">
      <v-textarea
        v-for="item, i in textAreaItems"
        :key="i"
        :value="item.value"
        readonly
        disabled
        outlined
        class="cursor"
      >
        <template #label>
          <h3>{{ item.label }}</h3>
        </template>
      </v-textarea>
    </template>
    <template v-else>
      <component
        :is="component"
        v-bind="{
          value: inputValue(component, value),
          items: inputValue(component, value),
          chips: true,
          multiple: true,
          disabled: true,
          readonly: true,
          outlined: true,
          hideDetails: true,
          class: 'cursor',
          label
        }"
      >
        <template
          v-if="component === 'v-text-field' || component === 'v-textarea'"
          #label
        >
          <h3>{{ label }}</h3>
        </template>
      </component>
    </template>
  </div>
</template>

<script>
import { toDateString } from '@/utils/helpers'
export default {
  name: 'ActivityLogsInput',
  props: {
    fieldName: {
      type: String,
      default: ''
    },
    component: {
      type: String,
      default: ''
    },
    value: {
      type: String,
      default: ''
    },
    newValue: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    }
  },
  data: () => ({
    howBreachOccurred: '1.a How breach occurred + DPS vulnerability',
    chronology: '1.b Chronology',
    numberOfDataSubjects: '1.c Number of DS / Records',
    descriptionOrNature: '1.d Description / Nature',
    likelyConsequences: '1.e Likely Consequences',
    dataProtectionOfficer: '1.f DPO',
    sensitivePersonalInformation: '2.a SPI',
    identityFraud: '2.b Other info that may enable identity fraud',
    measuresToAddress: '3.a Measures to address the breach',
    measuresToSecure: '3.b Measures to secure/recover personal data?',
    actionsToMitigateHarm: '3.c Actions to mitigate harm?',
    actionsToInformDataSubjects: '3.d Actions to inform data subjects?',
    measuresToPreventRecurrence: '3.e Measures to prevent recurrence of incidence',
    radioButtonValue: {
      isIncidentInvolve: {
        left: 'Yes',
        right: 'No'
      },
      exemptionToDSNotification: {
        left: 'Yes',
        right: 'No'
      },
      registration: {
        left: 'Registered',
        right: 'Not Registered'
      },
      'fbner.notificationType': {
        left: 'Voluntary',
        right: 'Mandatory'
      },
      evaluation: {
        left: 'Granted',
        right: 'Denied'
      },
      notifiedDataSubject: {
        left: 'Yes',
        right: 'No'
      }
    }
  }),
  computed: {
    toJson () {
      return string => {
        if (!string) return []
        return JSON.parse(string)
      }
    },
    noInfoItems () {
      const validVal = this.value === '{}' ? this.newValue : this.value
      const resultObj = this.toJson(validVal)
      const keys = Object.keys(resultObj)
      return keys.map(key => {
        return {
          label: this.$data[`${key}`],
          value: this.value === '{}' ? undefined : resultObj[`${key}`]
        }
      })
    },
    checkboxItems () {
      return (val) => {
        const validValue = this.value.length ? val : this.toJson(this.newValue)
        return validValue.map(item => {
          return { value: val.length ? item : null, label: item }
        })
      }
    },
    textAreaItems () {
      const resultObj = this.toJson(this.value || this.newValue)
      const keys = Object.keys(resultObj)
      const labels = {
        findings: 'Findings',
        referencesOrSupportingEvidence: 'References/Supporting Evidence',
        recommendations: 'Remarks'
      }
      return keys.map(key => {
        return {
          label: labels[`${key}`],
          value: this.value ? resultObj[`${key}`] : null
        }
      })
    },
    statusOfComplianceItems () {
      const objValue = this.toJson(this.value || this.newValue)
      const compliance = Object.keys(objValue)
      return compliance.map(key => {
        return {
          type: key === 'initialReport' ? 'Initial Report' : 'Full Report',
          statusOfCompliance: this.value && objValue[`${key}`].statusOfCompliance,
          dateOfCompliance: this.value && objValue[`${key}`].dateOfCompliance
        }
      })
    },
    items () {
      return this.toJson(this.value || this.newValue)
    }
  },
  methods: {
    inputValue (component, value) {
      if (component === 'v-autocomplete') return this.toJson(value)
      else if (component === 'checkbox') return { model: value === 'true' }
      else if (component === 'radio') return { model: value }
      else if (component === 'v-checkbox') return { model: this.toJson(value) }
      else if (component === 'v-text-field') return this.sanitizeString(value)
      return value
    },
    toDateString (date) {
      return toDateString(date)
    },
    sanitizeString (string) {
      const dirtyString = ['Invalid date']
      if (dirtyString.includes(string)) return ''
      return string
    }
  }
}
</script>

<style scoped>
.cursor >>>  textarea {
    overflow-y: hidden !important;
    cursor: default !important
}
</style>
